import type { WorkflowRunPauseState } from "./workflow-run-control";

export type WorkflowRunStatusStyle = {
  label: string;
  tone: "neutral" | "success" | "warning" | "error" | "info";
  pulse?: boolean;
};

const KNOWN: Record<string, WorkflowRunStatusStyle> = {
  // A freshly-enqueued run/step is stored as `pending` (SDK status) until a
  // worker picks it up — surfaced as "Queued" so it reads as "waiting", not work.
  pending: { label: "Queued", tone: "neutral" },
  queued: { label: "Queued", tone: "neutral" },
  running: { label: "Running", tone: "info", pulse: true },
  paused: { label: "Paused", tone: "warning", pulse: false },
  sleeping: { label: "Sleeping", tone: "info" },
  completed: { label: "Completed", tone: "success" },
  succeeded: { label: "Succeeded", tone: "success" },
  failed: { label: "Failed", tone: "error" },
  timed_out: { label: "Timed out", tone: "warning" },
  cancelled: { label: "Cancelled", tone: "neutral" },
  canceled: { label: "Cancelled", tone: "neutral" },
  interrupted: { label: "Interrupted", tone: "warning" },
};

/** Resolve presentation from the authoritative backend status alone. */
export function workflowRunStatusStyle(status: string): WorkflowRunStatusStyle {
  return KNOWN[status.toLowerCase()] ?? { label: status || "Unknown", tone: "neutral" };
}

export function workflowRunStatusPresentation(
  status: string,
  pauseState: WorkflowRunPauseState = null,
  pauseRequested = false,
): WorkflowRunStatusStyle & { tooltip: string | null } {
  const resolvedPauseState = pauseState ?? (pauseRequested ? "pausing" : null);
  return {
    ...workflowRunStatusStyle(status),
    tooltip: resolvedPauseState === "paused"
      ? "Paused. Resume continues from saved progress."
      : resolvedPauseState === "pausing"
        ? "Finishing in-progress work. Resume continues from saved progress."
        : null,
  };
}
